import { createContext, useContext, useMemo } from 'react';

import { useAui, useAuiState } from '@assistant-ui/react';

import {
  isErrorResult,
  isTextPart,
  isToolCallPart,
} from '@/components/assistant-ui/message-part-utils';
import { useToolGroupOverrides } from '@/components/assistant-ui/tool-group-overrides';
import {
  buildToolCallStatus,
  buildToolStatusFromFlags,
  type ToolCallApproval,
  ToolStatus,
} from '@/components/assistant-ui/tool-status';
import { createToolMatcher, type ToolMatcher } from '@/lib/utils/tool-matcher';

// --- Types ---

export interface ToolCallInfo {
  toolCallId: string;
  toolName: string;
  args: unknown;
  result: unknown;
  status: ToolStatus;
}

export interface ToolGroupState<TItem, TResult = unknown> {
  items: TItem[];
  results: TResult[];
  toolCalls: ToolCallInfo[];
  /** Tool calls within the current group boundaries only (for debug info positioning) */
  groupToolCalls: ToolCallInfo[];
  status: ToolStatus;
  shouldRender: boolean;
  /**
   * Whether the carousel should render inline at the tool group position.
   * When `deferCarousel` is 'after-text' or 'bottom' and there is no text before the tool group,
   * this is false — the carousel is deferred to the end of the message via
   * DeferredCarousels. When there IS preceding text (or `deferCarousel` is
   * 'none'), this is true and the carousel renders inline.
   */
  resultsReady: boolean;
  /** Whether there is a text part after the group in the message content. */
  hasTextAfterGroup: boolean;
}

/**
 * Controls which part of GroupedToolCarousel to render:
 * - `'carousel'`: Only the results carousel (deferred to end of message).
 * - `undefined` (no provider): Both loading and carousel render inline (default).
 */
export type ToolGroupRenderMode = 'carousel';

const RenderModeContext = createContext<ToolGroupRenderMode | undefined>(undefined);
export const RenderModeProvider = RenderModeContext.Provider;
export function useRenderMode(): ToolGroupRenderMode | undefined {
  return useContext(RenderModeContext);
}

export interface ToolGroupProps {
  startIndex: number;
  endIndex: number;
  children?: React.ReactNode;
  renderMode?: ToolGroupRenderMode;
}

export interface ToolCallMessagePart {
  type: 'tool-call';
  toolCallId: string;
  toolName: string;
  args: unknown;
  result?: unknown;
  isError?: boolean;
}

// --- Internal helpers ---

const EMPTY_STATE = {
  items: [],
  results: [],
  toolCalls: [],
  groupToolCalls: [],
  status: { type: 'complete' } as const,
  shouldRender: false,
  resultsReady: true,
  hasTextAfterGroup: false,
};

interface ProcessedToolCall<TItem, TResult> {
  toolCallInfo: ToolCallInfo;
  isRunning: boolean;
  hasError: boolean;
  items: TItem[];
  result: TResult | null;
}

interface ProcessToolCallParams<TItem, TResult> {
  call: ToolCallMessagePart;
  extractItems: (result: TResult) => TItem[];
  isGated?: boolean;
}

function processToolCall<TItem, TResult>({
  call,
  extractItems,
  isGated,
}: ProcessToolCallParams<TItem, TResult>): ProcessedToolCall<TItem, TResult> {
  const hasResult = call.result !== undefined;
  const hasError = (call.isError ?? false) || (hasResult && isErrorResult(call.result));
  const { toolCallId, toolName, args, result } = call;
  const status = buildToolCallStatus({
    result,
    hasError,
    isGated,
    approval: (call as { approval?: ToolCallApproval }).approval,
  });
  const isRunning = status.type === 'running';

  const toolCallInfo: ToolCallInfo = { toolCallId, toolName, args, result, status };

  if (isRunning || hasError) {
    return { toolCallInfo, isRunning, hasError, items: [], result: null };
  }

  let items: TItem[];
  try {
    items = extractItems(result as TResult) ?? [];
  } catch {
    items = [];
  }

  return {
    toolCallInfo,
    isRunning: false,
    hasError: false,
    items,
    result: result as TResult,
  };
}

// --- computeToolGroupState ---

/**
 * Controls how tool-call groups are merged across non-consecutive boundaries.
 * - `'none'`: strict — only tool calls within the current consecutive group.
 * - `'adjacent'`: merge groups separated only by non-text parts (reasoning, etc.).
 *   Text parts act as hard boundaries unless groups before the text would be
 *   deferred to the same position. This is the default.
 */
export type GroupBoundaryMode = 'none' | 'adjacent';

/**
 * Controls where the carousel renders when tool calls have no preceding text.
 * - `'none'`: inline at the tool call position.
 * - `'after-text'`: deferred to after the first text part following the group.
 * - `'bottom'`: deferred to the end of the message.
 */
export type DeferCarouselMode = 'none' | 'after-text' | 'bottom';

interface ComputeToolGroupStateParams<TItem, TResult> {
  messageContent: readonly unknown[] | undefined;
  startIndex: number;
  endIndex: number;
  isMatchingTool: ToolMatcher;
  extractItems: (result: TResult) => TItem[];
  ignoreGroupBoundaries: GroupBoundaryMode;
  deferCarousel?: DeferCarouselMode;
}

export function computeToolGroupState<TItem, TResult>({
  messageContent,
  startIndex,
  endIndex,
  isMatchingTool,
  extractItems,
  ignoreGroupBoundaries,
  deferCarousel = 'none',
}: ComputeToolGroupStateParams<TItem, TResult>): ToolGroupState<TItem, TResult> {
  if (!messageContent) return EMPTY_STATE as ToolGroupState<TItem, TResult>;

  const isMatching = (part: unknown): part is ToolCallMessagePart =>
    isToolCallPart(part) && isMatchingTool(part.toolName);

  const groupParts = messageContent.slice(startIndex, endIndex + 1);
  const groupMatchingCalls = groupParts.filter(isMatching);

  if (groupMatchingCalls.length === 0) {
    return EMPTY_STATE as ToolGroupState<TItem, TResult>;
  }

  const hasPrecedingText = messageContent.slice(0, startIndex).some(isTextPart);

  let shouldRender: boolean;
  let matchingCalls: ToolCallMessagePart[];

  if (ignoreGroupBoundaries === 'adjacent') {
    // Merge adjacent groups separated only by non-text parts (reasoning, etc.).
    // Only defer to a later group when it has completed (has a result) —
    // during streaming, render independently so the user sees intermediate
    // states (e.g. "no results" for an empty retry, then merged carousel
    // once the broader retry finishes).

    // Forward scan: check if there's a later adjacent completed match.
    // Text boundaries are skipped when both groups would render at the same
    // deferred position:
    // - 'bottom': all carousels go to the bottom → text never blocks.
    // - 'after-text': only skip the first text if this group has no preceding
    //   text (it would be deferred past that text to the same position).
    const skipTextInScan =
      deferCarousel === 'bottom' || (deferCarousel === 'after-text' && !hasPrecedingText);

    let deferToLater = false;
    let skippedTextForward = false;
    for (let i = endIndex + 1; i < messageContent.length; i++) {
      if (isTextPart(messageContent[i])) {
        if (deferCarousel === 'bottom') {
          continue; // all go to bottom — text never blocks
        }
        if (skipTextInScan && !skippedTextForward) {
          skippedTextForward = true;
          continue; // deferred past this text — keep scanning
        }
        break;
      }
      if (isMatching(messageContent[i])) {
        const tc = messageContent[i] as ToolCallMessagePart;
        deferToLater = tc.result !== undefined;
        break;
      }
    }

    if (deferToLater) {
      shouldRender = false;
      matchingCalls = [];
    } else {
      shouldRender = true;
      // Backward scan: collect matching calls through non-text separators.
      // Text boundaries are only crossed when the groups on the other side
      // would be deferred to the same rendered position:
      // - 'bottom': all carousels go to the bottom → text never blocks.
      // - 'after-text': only cross the first text boundary (groups before it
      //   have no preceding text → they'd be deferred to after that text).
      const merged: ToolCallMessagePart[] = [];
      for (let i = startIndex - 1; i >= 0; i--) {
        const part = messageContent[i];
        if (isTextPart(part)) {
          if (deferCarousel === 'bottom') {
            continue; // all go to bottom — text never blocks
          }
          // For 'after-text': only cross this text if groups before it have no
          // preceding text (they'd be deferred to after this text = our position).
          if (deferCarousel === 'after-text' && !messageContent.slice(0, i).some(isTextPart)) {
            continue;
          }
          break;
        }
        if (isMatching(part)) merged.push(part);
      }
      merged.reverse();
      matchingCalls = [...merged, ...groupMatchingCalls];
    }
  } else {
    shouldRender = true;
    matchingCalls = groupMatchingCalls;
  }

  const items: TItem[] = [];
  const results: TResult[] = [];
  const toolCalls: ToolCallInfo[] = [];
  const groupToolCalls: ToolCallInfo[] = [];
  let isRunning = false;
  let hasError = false;

  const isGated = messageContent.some(
    part => isToolCallPart(part) && !!(part as { approval?: unknown }).approval,
  );
  const processedMap = new Map<string, ToolCallInfo>();
  for (const call of matchingCalls) {
    const processed = processToolCall<TItem, TResult>({ call, extractItems, isGated });
    processedMap.set(call.toolCallId, processed.toolCallInfo);

    toolCalls.push(processed.toolCallInfo);
    items.push(...processed.items);

    if (processed.result !== null) {
      results.push(processed.result);
    }
    if (processed.isRunning) isRunning = true;
    if (processed.hasError) hasError = true;
  }

  // Reuse already-processed results when available. Falls back to processing
  // separately for non-last groups with ignoreGroupBoundaries (debug info positioning).
  for (const call of groupMatchingCalls) {
    const existing = processedMap.get(call.toolCallId);
    groupToolCalls.push(
      existing ?? processToolCall<TItem, TResult>({ call, extractItems, isGated }).toolCallInfo,
    );
  }

  let resultsReady = true;
  if (deferCarousel === 'bottom') {
    resultsReady = false;
  } else if (deferCarousel === 'after-text') {
    resultsReady = hasPrecedingText;
  }

  const hasTextAfterGroup = messageContent.slice(endIndex + 1).some(isTextPart);

  // When errors and successes are mixed, suppress the error state. The AI
  // retried (possibly with different args) and got a valid result — show
  // the results carousel or "no results" instead of the error.
  const hasSuccessfulCompletion = matchingCalls.some(
    c => c.result !== undefined && !c.isError && !isErrorResult(c.result),
  );
  const showError = hasError && !hasSuccessfulCompletion;

  const state = {
    items,
    results,
    toolCalls,
    groupToolCalls,
    status: buildToolStatusFromFlags({
      isRunning,
      hasError: showError,
    }),
    shouldRender,
    resultsReady,
    hasTextAfterGroup,
  };

  return state;
}

/**
 * Re-orders partitions so the one whose earliest tool call fired first
 * renders first. Builders that bucket by a derived key (origin, dest, …)
 * follow Map insertion order, which can disagree with tool-call order.
 *
 * @param partitions Partitions to sort. Each must expose `state.toolCalls`.
 * @param orderedToolCalls The canonical tool-call order to sort against.
 *   Partitions referencing unknown ids (or having no tool calls) sink to the
 *   end and preserve their relative input order.
 * @returns A new array, sorted; the input is not mutated.
 */
export function sortPartitionsByToolCallOrder<T extends { state: { toolCalls: ToolCallInfo[] } }>({
  partitions,
  orderedToolCalls,
}: {
  partitions: T[];
  orderedToolCalls: ToolCallInfo[];
}): T[] {
  const indexById = new Map(orderedToolCalls.map((tc, i) => [tc.toolCallId, i]));
  const firstIndex = (p: T) => {
    let min = Number.POSITIVE_INFINITY;
    for (const tc of p.state.toolCalls) {
      const idx = indexById.get(tc.toolCallId);
      if (idx !== undefined && idx < min) min = idx;
    }
    return min;
  };
  // Explicit comparison instead of `a.idx - b.idx` so two partitions whose
  // earliest index is `+Infinity` (no known tool calls) compare as equal
  // rather than producing `NaN`, which would leave order engine-dependent.
  return partitions
    .map(p => ({ p, idx: firstIndex(p) }))
    .sort((a, b) => (a.idx === b.idx ? 0 : a.idx < b.idx ? -1 : 1))
    .map(({ p }) => p);
}

// --- Group range helpers ---

export interface GroupRange {
  startIndex: number;
  endIndex: number;
}

export function getUniqueToolCallGroups(messageContent: readonly unknown[]): GroupRange[] {
  const groups: GroupRange[] = [];
  let i = 0;
  while (i < messageContent.length) {
    if (isToolCallPart(messageContent[i])) {
      const startIndex = i;
      while (i < messageContent.length && isToolCallPart(messageContent[i])) i++;
      groups.push({ startIndex, endIndex: i - 1 });
    } else {
      i++;
    }
  }
  return groups;
}

// --- React hook ---

export interface UseToolGroupStateOptions<TItem, TResult> {
  startIndex: number;
  endIndex: number;
  toolName?: string;
  isMatchingTool?: (toolName: string) => boolean;
  extractItems: (result: TResult) => TItem[];
  /**
   * Controls how tool-call groups are merged across non-consecutive boundaries.
   * - `'none'`: strict — only tool calls within the current consecutive group.
   * - `'adjacent'` (default): merge groups separated only by non-text parts
   *   (reasoning, etc.). Text parts act as hard boundaries.
   */
  ignoreGroupBoundaries?: GroupBoundaryMode;
  /**
   * Controls where carousels render when there is no preceding text.
   * - `'none'`: inline at the tool call position.
   * - `'after-text'`: deferred to after the first text part following the group.
   * - `'bottom'`: deferred to the end of the message.
   * Defaults to `'after-text'` when not explicitly set.
   */
  deferCarousel?: DeferCarouselMode;
}

export function useToolGroupState<TItem, TResult = unknown>({
  startIndex,
  endIndex,
  toolName,
  isMatchingTool: customMatcher,
  extractItems,
  ignoreGroupBoundaries: ignoreGroupBoundariesProp = 'adjacent',
  deferCarousel: deferCarouselProp,
}: UseToolGroupStateOptions<TItem, TResult>): ToolGroupState<TItem, TResult> {
  const api = useAui();
  const contextOverrides = useToolGroupOverrides(toolName);

  const ignoreGroupBoundaries = contextOverrides.ignoreGroupBoundaries ?? ignoreGroupBoundariesProp;

  const isMatchingTool = useMemo(
    () => customMatcher ?? (toolName ? createToolMatcher(toolName) : () => false),
    [customMatcher, toolName],
  );

  const deferCarousel: DeferCarouselMode =
    contextOverrides.deferCarousel ?? deferCarouselProp ?? 'after-text';

  // Subscribe to a fingerprint of only what computeToolGroupState depends on:
  // tool-call structure and whether text precedes the group (for deferCarousel).
  // Text-only changes (new text parts, streaming deltas) don't affect tool
  // group state, so excluding them avoids unnecessary re-computation.
  const contentFingerprint = useAuiState(({ message }) => {
    const content = message?.content;
    if (!content?.length) return '';
    const parts = content as readonly {
      type: string;
      toolCallId?: string;
      result?: unknown;
      isError?: boolean;
    }[];
    let fp = '';
    let hasTextBeforeGroup = false;
    let hasTextAfterGroup = false;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (part.type === 'tool-call') {
        if (fp) fp += '|';
        fp += `${i}:${part.toolCallId}:${part.result !== undefined}:${part.isError ?? false}`;
      } else if (part.type === 'text') {
        if (i < startIndex) hasTextBeforeGroup = true;
        if (i > endIndex) hasTextAfterGroup = true;
      }
    }
    const prefix = (hasTextBeforeGroup ? 'TB|' : '') + (hasTextAfterGroup ? 'TA|' : '');
    return prefix + fp;
  });

  return useMemo(
    () => {
      // Read content imperatively — the fingerprint subscription ensures
      // this only re-runs when tool-call structure actually changes.
      const messageContent = api.message.getState().content;
      return computeToolGroupState({
        messageContent,
        startIndex,
        endIndex,
        isMatchingTool,
        extractItems,
        ignoreGroupBoundaries,
        deferCarousel,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      contentFingerprint,
      api,
      startIndex,
      endIndex,
      isMatchingTool,
      extractItems,
      ignoreGroupBoundaries,
      deferCarousel,
    ],
  );
}
